import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Download, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BSRow, DisplayCurrency, Period } from "@/hooks/finance/balance-sheet/useBalanceSheet";
import { exportBalanceSheetToExcel, exportBalanceSheetToPdf } from "./exportExcel";

type Props = {
  rows: BSRow[];
  periods: Period[];
  isLoading: boolean;
  displayCurrency: DisplayCurrency;
};

function fmt(v: number, currency: DisplayCurrency): string {
  if (!Number.isFinite(v) || v === 0) return "–";
  const abs = Math.abs(v);
  const sign = currency === "USD" ? "$" : "ETB ";
  const num = abs.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return v < 0 ? `(${sign}${num})` : `${sign}${num}`;
}

export function BalanceSheetTable({ rows, periods, isLoading, displayCurrency }: Props) {
  return (
    <Card className="border-none shadow-lg overflow-hidden bg-white">
      <CardHeader className="bg-slate-900 border-none pb-4">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-white text-base">Balance Sheet</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() =>
                exportBalanceSheetToExcel(
                  rows,
                  periods,
                  displayCurrency,
                  `balance-sheet-${format(new Date(), "yyyyMMdd")}.xlsx`,
                )
              }
              title="Download as Excel with live formulas"
            >
              <Download className="h-3 w-3 mr-1" />
              Excel
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="h-6 text-[10px]"
              onClick={() =>
                exportBalanceSheetToPdf(
                  rows,
                  periods,
                  displayCurrency,
                  `balance-sheet-${format(new Date(), "yyyyMMdd")}.pdf`
                )
              }
              title="Download as PDF"
            >
              <FileText className="h-3 w-3 mr-1" />
              PDF
            </Button>
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px] uppercase font-black tracking-widest">
              {displayCurrency}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-32 bg-slate-50/20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="sticky left-0 z-20 bg-slate-50 w-72 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">
                    Line Item
                  </th>
                  {periods.map((p) => (
                    <th
                      key={p.key}
                      className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[120px]"
                    >
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const rowCls = cn(
                    row.isHeader && "bg-slate-100 font-black uppercase text-[10px] tracking-widest text-slate-600",
                    row.isTotal && "bg-slate-50 font-bold",
                    row.isSubtotal && "font-semibold border-t border-slate-200",
                    row.isCheck && "bg-amber-50 font-mono text-amber-900",
                  );
                  return (
                    <tr key={row.key} className={rowCls}>
                      <td
                        className={cn(
                          "sticky left-0 bg-inherit px-4 py-2 text-left whitespace-nowrap border-r border-slate-200",
                        )}
                        style={{ paddingLeft: `${16 + (row.indent ?? 0) * 16}px` }}
                      >
                        {row.label}
                      </td>
                      {periods.map((p) => {
                        const v = row.values.get(p.key) ?? 0;
                        if (row.isHeader) return <td key={p.key} />;
                        const isMismatch = row.isCheck && Math.abs(v) > 1;
                        return (
                          <td
                            key={p.key}
                            className={cn(
                              "px-4 py-2 text-right tabular-nums whitespace-nowrap",
                              isMismatch && "text-red-600 font-bold",
                            )}
                          >
                            {row.isCheck ? fmt(v, displayCurrency) : fmt(v, displayCurrency)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
